import { supabase } from '../../config/supabase';

/**
 * Get all deliveries assigned to a specific driver.
 * Supports both old-system jobs (matched by quote_type + delivery_count)
 * and new-system jobs (matched by child_unique_id UUID).
 * @param {string} driverId - The driver's user ID
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of quote-based delivery cards
 */
export const getMyDeliveries = async (driverId, companyId) => {
    try {
        const { data, error } = await supabase
            .from("job_deliveries")
            .select(`
                *,
                jobs!inner (
                    id,
                    company_id,
                    unique_id,
                    short_id,
                    customer_name,
                    customer_number,
                    customer_mail,
                    customer_address,
                    urgent_quote,
                    urgent_design,
                    deleted,
                    created_at,
                    is_cancelled,
                    job_quotes (
                        id,
                        quote_type,
                        is_required,
                        expected_delivery_date,
                        delivery_note,
                        delivery_time_slot,
                        delivery_count,
                        child_unique_id,
                        delivery_status
                    )
                )
            `)
            .eq("assigned_driver_id", driverId)
            .eq("jobs.company_id", companyId)
            .eq("jobs.deleted", false)
            .order("assigned_at", { ascending: false });

        if (error) throw error;

        return (data || []).map((delivery) => {
            const job = delivery.jobs;
            const quotes = job?.job_quotes || [];

            const deliveryCount = delivery.delivery_count ?? 0;

            const matchedQuote = delivery.child_id
                ? quotes.find(q => q.id === delivery.child_id)
                : quotes.find(q =>
                    q.quote_type === delivery.quote_type &&
                    (q.delivery_count ?? 0) === deliveryCount
                );

            return {
                card_id: delivery.id,
                job_id: job.id,

                unique_id: job.unique_id,
                short_id: job.short_id,
                customer_name: job.customer_name,
                customer_number: job.customer_number,
                customer_mail: job.customer_mail,
                customer_address: job.customer_address,
                urgent_quote: job.urgent_quote,
                urgent_design: job.urgent_design,
                created_at: job.created_at,
                is_cancelled: job.is_cancelled,

                quote_info: matchedQuote ? {
                    id: matchedQuote.id,
                    quote_type: matchedQuote.quote_type,
                    expected_delivery_date: matchedQuote.expected_delivery_date,
                    delivery_note: matchedQuote.delivery_note,
                    delivery_time_slot: matchedQuote.delivery_time_slot,
                    delivery_count: matchedQuote.delivery_count ?? 0,
                    child_unique_id: matchedQuote.child_unique_id || null,
                    delivery_status: matchedQuote.delivery_status,
                } : {
                    id: delivery.child_id || null,
                    quote_type: delivery.quote_type,
                    expected_delivery_date: delivery.delivery_date,
                    delivery_note: null,
                    delivery_time_slot: null,
                    delivery_count: deliveryCount,
                    child_unique_id: delivery.child_unique_id || null,
                    delivery_status: delivery.delivery_status,
                },

                delivery,
            };
        });
    } catch (error) {
        console.error("Error fetching my deliveries:", error);
        throw error;
    }
};

/**
 * Get all COMPLETED deliveries for a specific driver.
 * Supports both old-system and new-system (child_unique_id) jobs.
 * @param {string} driverId - The driver's user ID
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of completed quote-based delivery cards
 */
export const getMyCompletedDeliveries = async (driverId, companyId) => {
    try {
        const { data, error } = await supabase
            .from('jobs')
            .select(`
                *,
                job_quotes!inner (
                    id,
                    quote_type,
                    is_required,
                    expected_delivery_date,
                    delivery_note,
                    delivery_time_slot,
                    delivery_count,
                    child_unique_id
                ),
                job_deliveries!inner (
                    id,
                    quote_type,
                    assigned_driver_id,
                    assigned_driver_name,
                    delivery_status,
                    delivery_date,
                    delivery_time,
                    delivery_address,
                    delivery_contact_name,
                    delivery_contact_phone,
                    special_instructions,
                    delivery_notes,
                    delivered_at,
                    tracking_number,
                    carrier,
                    delivery_count,
                    child_id,
                    child_unique_id
                )
            `)
            .eq('company_id', companyId)
            .eq('job_deliveries.assigned_driver_id', driverId)
            .order('created_at', { ascending: false });

        if (error) throw error;

        // Transform to quote-based cards, filtering for completed only
        const quoteCards = [];
        data?.forEach(job => {
            const driverDeliveries = job.job_deliveries.filter(d => d.assigned_driver_id === driverId);

            driverDeliveries.forEach(delivery => {
                // Only include completed deliveries
                const isCompleted = delivery.delivery_status === 'delivered' || delivery.delivered_at !== null;
                if (!isCompleted) return;

                const isNewSystem = !!delivery.child_unique_id;

                let quote;
                if (isNewSystem) {
                    quote = job.job_quotes.find(q => q.id === delivery.child_unique_id);
                } else {
                    const deliveryCount = delivery.delivery_count ?? 0;
                    quote = job.job_quotes.find(q =>
                        q.quote_type === delivery.quote_type &&
                        (q.delivery_count ?? 0) === deliveryCount
                    );
                }

                if (quote) {
                    quoteCards.push({
                        ...job,
                        quote_info: {
                            id: quote.id,
                            quoteId: quote.id,
                            quote_type: quote.quote_type,
                            expected_delivery_date: quote.expected_delivery_date,
                            is_required: quote.is_required,
                            delivery_note: quote.delivery_note,
                            delivery_time_slot: quote.delivery_time_slot,
                            delivery_count: quote.delivery_count ?? 0,
                            childUniqueId: quote.child_unique_id || null,
                        },
                        delivery: delivery,
                        job_quotes: job.job_quotes,
                        job_deliveries: job.job_deliveries
                    });
                }
            });
        });

        return quoteCards;
    } catch (error) {
        console.error('Error fetching completed deliveries:', error);
        throw error;
    }
};

/**
 * Subscribe to delivery updates for a driver
 * @param {string} companyId - Company ID
 * @param {Function} callback - Callback function when updates occur
 * @returns {RealtimeChannel} Supabase channel
 */
export const subscribeToMyDeliveryUpdates = (companyId, callback) => {
    const channel = supabase
        .channel(`deliveries-${companyId}-${Date.now()}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_deliveries',
            },
            (payload) => {
                callback(payload);
            }
        )
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'jobs',
                filter: `company_id=eq.${companyId}`
            },
            (payload) => {
                callback(payload);
            }
        )
        .subscribe();

    return channel;
};

/**
 * Check if a delivery is completed by the driver
 * @param {Object} delivery - The delivery object from job_deliveries
 * @returns {boolean} True if completed
 */
export const isDeliveryCompleted = (delivery) => {
    return delivery?.delivery_status === 'delivered' || delivery?.delivered_at !== null;
};

/**
 * Update delivery status
 * @param {string} deliveryId - The delivery ID
 * @param {string} status - The new status (not_scheduled, scheduled, in_transit, delivered, cancelled)
 * @param {string} deliveryNotes - Optional delivery notes
 * @param {string} userId - User ID updating the status
 * @param {string} userName - User name updating the status
 * @returns {Promise<Object>} Updated delivery
 */
export const updateDeliveryStatus = async (deliveryId, status, deliveryNotes = null, userId = null, userName = null) => {
    try {
        const updateData = {
            delivery_status: status,
        };

        if (deliveryNotes !== null) {
            updateData.delivery_notes = deliveryNotes;
        }

        if (status === 'delivered') {
            updateData.delivered_at = new Date().toISOString();
            if (userId) updateData.delivered_by_user_id = userId;
            if (userName) updateData.delivered_by_name = userName;
        }

        const { data, error } = await supabase
            .from('job_deliveries')
            .update(updateData)
            .eq('id', deliveryId)
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error updating delivery status:', error);
        throw error;
    }
};
